import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatEuros } from "@/lib/format";
import { Bloc, Chiffre, Barre, Fleche, pct, Vide } from "./commun";

export const Opportunites = ({ o }) => (
  <Bloc titre="Opportunités créées" sous={`Mois précédent à la même date : ${o.mois_precedent_meme_date} · sur tout le mois : ${o.mois_precedent}`} testId="kpi-opportunities">
    <div className="grid grid-cols-3 gap-2">
      <Chiffre label="Hier" valeur={o.hier} testId="kpi-opp-yesterday" />
      <Chiffre label="Cette semaine" valeur={o.semaine} testId="kpi-opp-week" />
      <Chiffre label="Ce mois" valeur={o.mois} testId="kpi-opp-month" />
    </div>
    <p className="text-sm mt-3 flex items-center gap-1.5" data-testid="kpi-opp-evolution">
      <Fleche v={o.evolution} />{o.evolution === null ? "Pas de comparaison possible" : `${o.evolution > 0 ? "+" : ""}${o.evolution} % par rapport au mois précédent`}
    </p>
  </Bloc>
);

export const Prevision = ({ p, testId = "kpi-forecast" }) => (
  <Bloc titre="Devis signés et prévision de fin de mois" sous={`Basée sur les devis signés, au rythme des ${Math.floor(p.jours_ecoules)} premiers jours sur ${p.jours_mois}`} testId={testId}>
    <div className="grid grid-cols-2 gap-2">
      <Chiffre label="Signés ce mois" valeur={p.signes} sous={formatEuros(p.montant)} testId={`${testId}-signed`} />
      <Chiffre label="Prévision fin de mois" valeur={p.prevision_nombre} sous={formatEuros(p.prevision_montant)} testId={`${testId}-projection`} />
    </div>
    <div className="mt-3"><Barre pct={p.prevision_montant ? (100 * p.montant) / p.prevision_montant : 0} /></div>
  </Bloc>
);

export const Annuel = ({ annuel, annee }) => (
  <Bloc titre={`Vue annuelle ${annee}`} sous="Opportunités créées et devis signés par mois" testId="kpi-annual">
    <div className="h-56 -ml-3">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={annuel} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis dataKey="mois" tick={{ fontSize: 10, fill: "var(--muted)" }} interval={0} angle={-45} textAnchor="end" height={40} />
          <YAxis tick={{ fontSize: 10, fill: "var(--muted)" }} width={32} allowDecimals={false} />
          <Tooltip contentStyle={{ background: "var(--surface)", border: "none", borderRadius: 12, color: "var(--ink)" }} cursor={{ fill: "var(--surface-2)" }} />
          <Bar dataKey="opportunites" name="Opportunités" fill="var(--accent)" radius={[6, 6, 0, 0]} />
          <Bar dataKey="signes" name="Devis signés" fill="var(--success)" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Bloc>
);

const TEMPS = ["var(--temp-1)", "var(--temp-2)", "var(--temp-3)", "var(--temp-5)", "var(--temp-6)", "var(--temp-7)"];

export const EntonnoirTaux = ({ etapes, periode, setPeriode }) => {
  const max = Math.max(1, ...etapes.map((e) => e.nombre));
  return (
    <Bloc titre="Entonnoir complet" sous="Avec le taux de passage d'une marche à l'autre" testId="kpi-funnel"
      action={<div className="flex gap-1 shrink-0">{[["mois", "Mois"], ["annee", "Année"]].map(([c, l]) => (
        <button key={c} type="button" onClick={() => setPeriode(c)} className="min-h-[44px] px-3 at-pill text-sm font-semibold" data-testid={`funnel-period-${c}`}
          style={{ background: periode === c ? "var(--accent)" : "var(--surface-2)", color: periode === c ? "var(--accent-ink)" : "var(--ink)" }}>{l}</button>))}</div>}>
      <ol className="space-y-2">
        {etapes.map((e, i) => (
          <li key={e.etape} data-testid={`funnel-step-${i}`}>
            <div className="flex justify-between gap-2 text-sm"><span className="font-semibold truncate">{e.etape}</span><span className="tabular-nums shrink-0">{e.nombre}{e.taux !== null && <span className="at-muted"> · {e.taux} %</span>}</span></div>
            <Barre pct={(100 * e.nombre) / max} couleur={TEMPS[i]} />
          </li>
        ))}
      </ol>
    </Bloc>
  );
};

export const VueGlobale = ({ v }) => (
  <Bloc titre="Vue globale des processus" sous="Fiches en retard ou sans action depuis 7 jours : où ça bloque" testId="kpi-global-view">
    {v.blocages.length > 0 && (
      <div className="space-y-1 mb-4" data-testid="kpi-blockers">
        {v.blocages.map((b, i) => (
          <p key={i} className="text-sm flex gap-2 items-start"><span className="w-2.5 h-2.5 at-pill mt-1.5 shrink-0" style={{ background: b.temperature || "var(--muted)" }} />
            <span className="break-words"><b>{b.bloquees}</b> bloquée(s) · {b.processus} › {b.etape}{b.partenaire ? ` · chez ${b.partenaire}` : ""}</span></p>
        ))}
      </div>
    )}
    {v.processus.length === 0 && <Vide />}
    <div className="space-y-4">
      {v.processus.map((p) => (
        <div key={p.id} data-testid={`global-process-${p.id}`}>
          <p className="text-sm font-semibold mb-1.5">{p.nom} <span className="at-muted font-normal">· {p.total} en cours</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {p.etapes.map((e) => (
              <div key={e.code} className="at-soft p-2.5 min-w-0" style={{ borderLeft: `4px solid ${e.temperature || "var(--muted)"}` }}>
                <p className="text-[11px] at-muted truncate">{e.nom}</p>
                <p className="font-bold">{e.nombre}{e.bloquees > 0 && <span className="text-xs" style={{ color: "var(--danger)" }}> · {e.bloquees} bloquée(s)</span>}</p>
                {e.partenaires.map((x) => <p key={x.nom} className="text-[11px] truncate" style={{ color: "var(--danger)" }}>{x.nom} : {x.bloquees}</p>)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Bloc>
);

export const Productivite = ({ d, periode, setPeriode }) => (
  <Bloc titre="Productivité par agent" sous="Appels croisés avec les fiches du CRM" testId="agent-productivity"
    action={<div className="flex gap-1 shrink-0">{[["jour", "Jour"], ["semaine", "Sem."], ["mois", "Mois"]].map(([c, l]) => (
      <button key={c} type="button" onClick={() => setPeriode(c)} className="min-h-[44px] px-2.5 at-pill text-sm font-semibold" data-testid={`productivity-period-${c}`}
        style={{ background: periode === c ? "var(--accent)" : "var(--surface-2)", color: periode === c ? "var(--accent-ink)" : "var(--ink)" }}>{l}</button>))}</div>}>
    {d.agents.length === 0 && <Vide />}
    <div className="space-y-4">
      {d.agents.map((a) => (
        <div key={a.agent_id} className="at-soft p-3 space-y-2" data-testid={`productivity-agent-${a.agent_id}`}>
          <p className="font-semibold">{a.agent}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <p>Appels : <b data-testid={`productivity-calls-${a.agent_id}`}>{a.appels}</b></p><p>Joints : <b>{a.contacts_joints}</b></p>
            <p>Décroché : <b>{pct(a.taux_decroche)}</b></p><p>Opportunités : <b>{a.opportunites}</b> ({a.validees} validée(s))</p>
          </div>
          <div className="flex flex-wrap gap-1.5">{a.statuts.map((s) => <span key={s.statut} className="text-[11px] at-pill px-2 py-1" style={{ background: "var(--surface)" }}>{s.statut} : {s.nombre}</span>)}</div>
        </div>
      ))}
    </div>
  </Bloc>
);
